import { stratify, tree } from 'd3-hierarchy';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import type { Node, NodeWithPosition } from './layoutTypes';
import {
  Direction,
  Edge,
  GraphPositions,
  LayoutAlgorithm,
  findConnectedGraphs,
  getIncomers,
} from './layoutTypes';

// D3 Hierarchy doesn't support layouting in different directions, but we can
// swap the coordinates around in different ways to get the same effect.
const getPosition = (x: number, y: number, direction: Direction) => {
  switch (direction) {
    case 'TB':
      return { x, y };
    case 'LR':
      return { x: y, y: x };
    case 'BT':
      return { x: -x, y: -y };
    case 'RL':
      return { x: -y, y: x };
    default:
      return finleyAssertUnreachable(direction);
  }
};

// Initialize the tree layout (see https://observablehq.com/@d3/tree for examples)
const layout = tree<NodeWithPosition>()
  // By default, d3 hierarchy spaces nodes that do not share a common parent quite
  // far apart. We think it looks a bit nicer (and more similar to the other layouting
  // algorithms) if we fix that distance to a uniform `1`.
  .separation(() => 1);

// D3 Hierarchy expects a single root node in a flow. Because we can't always
// guarantee that, we create a fake root node here and will make sure any real
// nodes without an incoming edge will get connected to this fake root node.
const rootNode = {
  id: 'd3-hierarchy-root',
  x: 0,
  y: 0,
  position: { x: 0, y: 0 },
};

const d3HierarchyLayout: LayoutAlgorithm = (nodes, edges, options) => {
  const isHorizontal = options.direction === 'RL' || options.direction === 'LR';

  const initialNodes = [] as NodeWithPosition[];

  for (const node of nodes) {
    const nodeWithPosition = { ...node, x: 0, y: 0 };
    initialNodes.push(nodeWithPosition);
  }

  // When the layout is horizontal, we swap the width and height measurements we
  // pass to the layout algorithm so things stay spaced out nicely. By adding the
  // amount of spacing to each size we can fake padding between nodes.
  const nodeSize = isHorizontal
    ? [options.spacing[1], options.spacing[0]]
    : [options.spacing[0], options.spacing[1]];
  layout.nodeSize(nodeSize as [number, number]);

  const getParentId = (node: Node) => {
    if (node.id === rootNode.id) {
      return undefined;
    }

    const incomers = getIncomers(node, nodes, edges);

    // If there are no incoming edges, we say this node is connected to the fake
    // root node to prevent having multiple root nodes in the layout. If there
    // are multiple incoming edges, only the first one will be used!
    return incomers[0]?.id || rootNode.id;
  };

  const hierarchy = stratify<NodeWithPosition>()
    .id((d) => d.id)
    .parentId(getParentId)([rootNode, ...initialNodes]);

  // We create a map of the laid out nodes here to avoid multiple traversals when
  // looking up a node's position later on.
  const root = layout(hierarchy);
  const returnValue: GraphPositions = {};
  for (const node of root) {
    const { x, y } = node;
    returnValue[node.id!] = getPosition(x, y, options.direction);
  }

  return returnValue;
};

export const repositionGraph = (params: {
  nodes: string[];
  edges: Edge[];
  xSpacing: number;
  ySpacing: number;
  disconnectedSpacing: number;
}): GraphPositions => {
  const { nodes, edges, xSpacing, ySpacing, disconnectedSpacing } = params;

  const results: GraphPositions = {};
  let maxY = 0;
  const connectedGraphs = findConnectedGraphs({ nodes, edges });
  connectedGraphs.forEach((subNodes) => {
    const d3Nodes = subNodes.map((id) => ({ id }));
    const subResults = d3HierarchyLayout(d3Nodes, edges, {
      direction: 'LR',
      spacing: [xSpacing, ySpacing],
    });

    let newMaxY = 0;
    // update subResults y values to consider previous graphs
    Object.entries(subResults).forEach(([key, { x, y }]) => {
      const updatedY = y + maxY;
      results[key] = { x, y: updatedY };
      newMaxY = Math.max(updatedY, newMaxY);
    });
    maxY = newMaxY + disconnectedSpacing;
  });
  return results;
};
